import { useLPSuspenseQuery } from '../lib/hooks/useLPQuery';
import type { LeaderboardUser, LeaderboardDepartment } from '../lib/types/LeaderboardUser';

type TopQueryParams = {
  mode: 'top';
  type: LeaderboardTypeParam;
  count: number;
  isManager?: boolean;
};

type PagedQueryParams = {
  mode: 'paged';
  type: LeaderboardTypeParam;
  skip: number;
  take: number;
  isManager?: boolean;
};

export enum LeaderboardTypeParam {
  TEAM = 'team',
  DEPARTMENT = 'department',
  COMPANY = 'company',
}

type LeaderboardQueryParams = TopQueryParams | PagedQueryParams;

export type LeaderboardPagedResults<T = LeaderboardUser> = {
  total: number;
  skip: number | null;
  take: number | null;
  values: T[];
};

export const useLeaderboardQuery = (params: LeaderboardQueryParams) => {
  const { type, mode, isManager } = params;

  let queryKey: (string | LeaderboardQueryParams)[];
  let endpoint: string;

  if (mode === 'top') {
    const { count } = params;
    queryKey = ['LeaderboardQuery', params];
    endpoint = isManager
      ? `/leaderboards/manager/${type}/top`
      : `/leaderboards/${type}/top?count=${count}`;
  } else {
    const { skip, take } = params;
    queryKey = ['LeaderboardQuery', params];
    endpoint = isManager
      ? `/leaderboards/manager/${type}?skip=${skip}&take=${take}`
      : `/leaderboards/${type}?skip=${skip}&take=${take}`;
  }

  // Use a single hook call to avoid React hooks rule violation
  const result = useLPSuspenseQuery<
    (LeaderboardUser | LeaderboardDepartment)[] |
    LeaderboardPagedResults<LeaderboardUser | LeaderboardDepartment>
  >(queryKey, endpoint);

  // Type assertion based on the type parameter for better type safety at runtime
  if (type === LeaderboardTypeParam.DEPARTMENT) {
    return result as {
      data: LeaderboardDepartment[] | LeaderboardPagedResults<LeaderboardDepartment>;
      error: Error | null;
      isFetching: boolean;
      refetch: () => void;
    };
  }

  return result as {
    data: LeaderboardUser[] | LeaderboardPagedResults<LeaderboardUser>;
    error: Error | null;
    isFetching: boolean;
    refetch: () => void;
  };
};
